/**
 * UnauthorizedPage Component
 *
 * Displayed when a user tries to access a resource they don't have permission for.
 * Provides a clear message and navigation back to appropriate dashboard.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Container } from '../components/layout';
import { Button, Card, CardBody } from '../components/ui';
import { useAuth, getDashboardPath } from '../contexts/AuthContext';

export const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const handleGoBack = () => {
    if (isAuthenticated && user) {
      // Navigate to user's appropriate dashboard
      navigate(getDashboardPath(user.role));
    } else {
      // Not logged in, go to login
      navigate('/login');
    }
  };

  return (
    <Layout>
      <Container maxWidth="md">
        <div
          className="flex flex-col items-center justify-center"
          style={{ minHeight: '60vh', padding: 'var(--space-12) 0' }}
        >
          <Card style={{ width: '100%' }}>
            <CardBody>
              <div className="text-center">
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto var(--space-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--color-warning, #f59e0b)',
                    color: 'var(--color-bg)',
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>

                <h2 style={{ marginBottom: 'var(--space-4)' }}>
                  Access Denied
                </h2>

                <p
                  className="text-muted"
                  style={{
                    fontSize: 'var(--text-lg)',
                    marginBottom: 'var(--space-6)',
                  }}
                >
                  You do not have permission to access this page.
                </p>

                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--warning-bg)',
                    border: 'var(--border-thick)',
                    borderColor: 'var(--color-warning, #f59e0b)',
                    marginBottom: 'var(--space-8)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-sm)',
                    textAlign: 'left',
                  }}
                >
                  <strong>What happened?</strong>
                  <br />
                  Your current role does not have the required permissions to view this resource.
                  If you believe this is an error, please contact your administrator.
                </div>

                <div className="flex gap-4 justify-center">
                  <Button
                    variant="primary"
                    onClick={handleGoBack}
                  >
                    {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
                  </Button>
                </div>

                {user && (
                  <div
                    style={{
                      marginTop: 'var(--space-8)',
                      padding: 'var(--space-4)',
                      backgroundColor: '#f9fafb',
                      border: 'var(--border-thin)',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--color-muted)',
                        margin: 0,
                      }}
                    >
                      Logged in as: <strong>{user.email}</strong>
                      <br />
                      Role: <strong>{user.role.replace('_', ' ')}</strong>
                    </p>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>
    </Layout>
  );
};

export default UnauthorizedPage;
